import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Button, Modal } from '@/components';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';
import {
  scheduledInstantAPI,
  type CreateSlotRiderInput,
  type SiSchedule,
  type SiScheduleDetail,
  type SiSlotSummary,
} from '@/services/api/scheduled-instant';

import { RiderPicker, type RiderSelection } from './pickers';

const DAY_LABELS = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'];

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const RiderCards: React.FC<{
  riders: { riderCode: string; riderName: string }[];
  reserve?: boolean;
}> = ({ riders, reserve }) => {
  if (riders.length === 0)
    return (
      <p className="text-[11px] text-gray-400 italic">— belum ditetapkan</p>
    );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {riders.map((r) => (
        <div
          key={r.riderCode}
          className="flex items-center gap-2 rounded-gsm border border-gray-200 bg-white px-2.5 py-1.5"
        >
          <span
            className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
              reserve
                ? 'bg-(--theme-accent-alpha-10) text-accent'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {initials(r.riderName)}
          </span>
          <div className="min-w-0">
            <div className="text-xs font-medium text-ink truncate">
              {r.riderName}
            </div>
            <div className="font-mono text-[10px] text-gray-400 truncate">
              {r.riderCode}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Detail Jadwal (Screen 8) — a large right drawer over Master Jadwal.
 * Shows the schedule's roster (editable, cascades to future slots via D3),
 * its change history, and its recent generated slots (each editable via
 * EditSlotModal, wired by the parent through onEditSlot).
 */
const ScheduleDetailDrawer: React.FC<{
  scheduleId: string | null;
  onClose: () => void;
  onChanged: () => void;
  onEditSchedule: (s: SiSchedule) => void;
  onDeactivate: (s: SiSchedule) => void;
  onEditSlot?: (slotId: string) => void;
}> = ({ scheduleId, onClose, onChanged, onEditSchedule, onDeactivate, onEditSlot }) => {
  const open = !!scheduleId;
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<SiScheduleDetail | null>(null);
  const [recent, setRecent] = useState<SiSlotSummary[]>([]);

  const [editRoster, setEditRoster] = useState(false);
  const [utama, setUtama] = useState<RiderSelection[]>([]);
  const [cadangan, setCadangan] = useState<RiderSelection[]>([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    setEditRoster(false);
    try {
      const d = await scheduledInstantAPI.getSchedule(scheduleId);
      setDetail(d);
      const toSel = (r: {
        riderCode: string;
        riderName: string;
        riderPhone: string;
      }) => ({
        riderCode: r.riderCode,
        riderName: r.riderName,
        riderPhone: r.riderPhone,
      });
      setUtama(d.utamaRiders.map(toSel));
      setCadangan(d.cadanganRiders.map(toSel));
      // Recent generated slots for this schedule (Riwayat Slot Terbaru).
      const recentSlots = await scheduledInstantAPI.listSlots({
        start_date: dayjs().subtract(21, 'day').format('YYYY-MM-DD'),
        end_date: dayjs().add(2, 'day').format('YYYY-MM-DD'),
        schedule_id: scheduleId,
      });
      setRecent(recentSlots.slice().reverse());
    } catch (e) {
      showErrorToast('Gagal memuat detail', (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    if (scheduleId) load();
    else setDetail(null);
  }, [scheduleId, load]);

  const saveRoster = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const s = detail.schedule;
      const riders: CreateSlotRiderInput[] = [
        ...utama.map((r) => ({ ...r, role: 'utama' as const })),
        ...cadangan.map((r) => ({ ...r, role: 'cadangan' as const })),
      ];
      await scheduledInstantAPI.updateSchedule(s.id, {
        providerId: s.providerId,
        clientName: s.clientName,
        windowStartTime: s.windowStartTime,
        windowEndTime: s.windowEndTime,
        recurringWeekdays: s.recurringWeekdays,
        batchLabel: s.batchLabel,
        riders,
        reason: reason.trim() || 'Rider diperbarui',
      });
      showSuccessToast('Rider diperbarui', s.clientName);
      setReason('');
      await load();
      onChanged();
    } catch (e) {
      showErrorToast('Gagal menyimpan rider', (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    setDeleting(true);
    try {
      await scheduledInstantAPI.deleteSchedule(detail.schedule.id);
      showSuccessToast('Jadwal dihapus', detail.schedule.clientName);
      onChanged();
      onClose();
    } catch (e) {
      showErrorToast('Gagal menghapus jadwal', (e as Error).message);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const s = detail?.schedule;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      position="right"
      width="xl"
      icon="ri-calendar-schedule-line"
      iconTone="primary"
      title={s ? `${s.clientName} · ${s.windowStartTime}–${s.windowEndTime}` : 'Detail Jadwal'}
      subtitle={
        s
          ? `Zero Tolerance · ${s.recurringWeekdays
              .map((d) => DAY_LABELS[d - 1])
              .join(', ')}`
          : ''
      }
      headerExtra={
        s ? (
          <Badge tone={s.isActive ? 'success' : 'neutral'} dot>
            {s.isActive ? 'Aktif' : 'Non-Aktif'}
          </Badge>
        ) : undefined
      }
    >
      {loading || !s ? (
        <p className="text-sm text-gray-500 py-8 text-center">Memuat…</p>
      ) : (
        <div className="space-y-5">
          {/* Actions */}
          {confirmDelete ? (
            <div className="rounded-gsm bg-red-50 border border-red-200 px-3 py-2.5 flex items-center justify-between gap-3">
              <p className="text-[12px] text-red-800 font-medium">
                Hapus jadwal ini? Slot yang sudah ada tetap tersimpan.
              </p>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  variant="white"
                  size="xs"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                >
                  Batal
                </Button>
                <Button
                  variant="danger"
                  size="xs"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Menghapus…' : 'Ya, Hapus'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="white" size="small" onClick={() => onEditSchedule(s)}>
                <i className="ri-edit-line mr-1" /> Edit Jadwal
              </Button>
              {s.isActive && (
                <Button
                  variant="outline-danger"
                  size="small"
                  onClick={() => onDeactivate(s)}
                >
                  Nonaktifkan
                </Button>
              )}
              <Button
                variant="ghost"
                size="small"
                onClick={() => setConfirmDelete(true)}
              >
                <i className="ri-delete-bin-line mr-1 text-red-500" />
                <span className="text-red-500">Hapus Jadwal</span>
              </Button>
            </div>
          )}

          {/* Roster */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold text-ink">Rider Ditugaskan</h4>
              {!editRoster ? (
                <Button
                  variant="outline-accent"
                  size="xs"
                  onClick={() => setEditRoster(true)}
                >
                  <i className="ri-edit-line mr-1" /> Edit Rider
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button
                    variant="white"
                    size="xs"
                    onClick={() => {
                      setEditRoster(false);
                      setUtama(detail.utamaRiders.map((r) => ({ ...r })));
                      setCadangan(detail.cadanganRiders.map((r) => ({ ...r })));
                    }}
                    disabled={saving}
                  >
                    Batal
                  </Button>
                  <Button
                    size="xs"
                    onClick={saveRoster}
                    disabled={saving || utama.length === 0}
                  >
                    {saving ? 'Menyimpan…' : 'Simpan'}
                  </Button>
                </div>
              )}
            </div>

            {editRoster ? (
              <div className="space-y-3">
                <div className="rounded-gsm bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
                  Perubahan rider akan diterapkan ke slot mendatang. Rider yang
                  sudah dikonfirmasi tidak akan dihapus.
                </div>
                <RiderPicker
                  open={editRoster}
                  title="Rider Utama"
                  required
                  riders={utama}
                  onChange={setUtama}
                  disabled={saving}
                />
                <RiderPicker
                  open={editRoster}
                  title="Rider Cadangan"
                  hint="opsional"
                  riders={cadangan}
                  onChange={setCadangan}
                  disabled={saving}
                />
                <input
                  className="w-full rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  placeholder="Alasan perubahan (opsional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={saving}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-[11px] font-semibold text-gray-500 mb-1.5">
                    Rider Utama ({detail.utamaRiders.length})
                  </p>
                  <RiderCards riders={detail.utamaRiders} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-gray-500 mb-1.5">
                    ⭐ Rider Cadangan ({detail.cadanganRiders.length})
                  </p>
                  <RiderCards riders={detail.cadanganRiders} reserve />
                </div>
              </div>
            )}
          </div>

          {/* Change history */}
          <div>
            <h4 className="text-sm font-bold text-ink mb-2">
              Riwayat Perubahan Jadwal
            </h4>
            {detail.changes.length === 0 ? (
              <p className="text-[11px] text-gray-400 italic">
                Belum ada perubahan.
              </p>
            ) : (
              <div className="space-y-2">
                {detail.changes.map((c) => (
                  <div key={c.id} className="flex gap-2 text-xs">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                    <div>
                      <span className="font-semibold text-ink">
                        {c.changeType}
                      </span>
                      {c.reason && (
                        <span className="text-gray-600"> — {c.reason}</span>
                      )}
                      <div className="text-[10.5px] text-gray-400">
                        {dayjs(c.createdAt).format('DD MMM YYYY HH:mm')} ·{' '}
                        {c.actor}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent slots */}
          <div>
            <h4 className="text-sm font-bold text-ink mb-2">
              Riwayat Slot Terbaru
            </h4>
            {recent.length === 0 ? (
              <p className="text-[11px] text-gray-400 italic">
                Belum ada slot dibuat.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-gsm border border-gray-100">
                <table className="w-full text-sm">
                  <tbody>
                    {recent.map((row) => (
                      <tr key={row.slot.id} className="border-b border-gray-50">
                        <td className="px-3 py-2 tabular text-gray-600 whitespace-nowrap">
                          {dayjs(row.slot.scheduleDate).format('DD MMM')}
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-[11px] text-gray-500">
                            {row.riderCount} rider
                          </span>{' '}
                          <StatusChips row={row} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          {onEditSlot && (
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => onEditSlot(row.slot.id)}
                            >
                              Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

const StatusChips: React.FC<{ row: SiSlotSummary }> = ({ row }) => (
  <span className="inline-flex items-center gap-1.5 align-middle">
    {row.counts.red > 0 && (
      <span className="text-[10px] text-red-600">● {row.counts.red}</span>
    )}
    {row.counts.amber > 0 && (
      <span className="text-[10px] text-amber-600">● {row.counts.amber}</span>
    )}
    {row.counts.green > 0 && (
      <span className="text-[10px] text-green-600">● {row.counts.green}</span>
    )}
  </span>
);

export default ScheduleDetailDrawer;
